'use client';

import { useCart } from '@/features/checkout/CartProvider';
import {
  getLocaleFileKey,
  localeCookieKey,
  resolveLocale,
} from '@/lib/i18n/config';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { forwardRef, useMemo, useState, useTransition } from 'react';
import { Dropdown } from 'react-bootstrap';

const LOCALE_OPTIONS = [
  { value: 'fr-SN', shortLabel: 'SN', flag: '🇸🇳' },
  { value: 'fr', shortLabel: 'FR', flag: '🇫🇷' },
  { value: 'en', shortLabel: 'EN', flag: '🇺🇸' },
];

const menuVariants = {
  hidden: (reducedMotion) => ({
    opacity: 0,
    scale: reducedMotion ? 1 : 0.92,
    pointerEvents: 'none',
  }),
  show: (reducedMotion) => ({
    opacity: 1,
    scale: 1,
    pointerEvents: 'auto',
    transition: { duration: reducedMotion ? 0.15 : 0.22 },
  }),
  exit: (reducedMotion) => ({
    opacity: 0,
    scale: reducedMotion ? 1 : 0.96,
    transition: { duration: 0.14 },
  }),
};

const MotionMenu = forwardRef(
  (
    {
      show,
      renderOnMount,
      rootCloseEvent,
      flip,
      align,
      close,
      popperConfig,
      bsPrefix,
      ...rest
    },
    ref,
  ) => <motion.div ref={ref} {...rest} />,
);

MotionMenu.displayName = 'MotionMenu';

function buildCurrentUrl(pathname, searchParams) {
  const query = searchParams?.toString();
  return query ? `${pathname}?${query}` : pathname;
}

export default function LanguageSwitcher({
  currentLocale,
  ariaLabel = 'Change language',
  optionLabels = {},
  className = '',
  compactOnMobile = true,
}) {
  const router = useRouter();
  const pathname = usePathname() || '/';
  const searchParams = useSearchParams();
  const reduceMotion = useReducedMotion();
  const { resetCart } = useCart();
  const normalizedLocale = resolveLocale(currentLocale);
  const [selectedLocale, setSelectedLocale] = useState(normalizedLocale);
  const [open, setOpen] = useState(false);
  const [isPending, startTransition] = useTransition();

  const activeOption = useMemo(
    () =>
      LOCALE_OPTIONS.find((option) => option.value === selectedLocale) ||
      LOCALE_OPTIONS[0],
    [selectedLocale],
  );

  const handleChange = (nextValue) => {
    const nextLocale = resolveLocale(nextValue);

    if (nextLocale === selectedLocale) {
      setOpen(false);
      return;
    }

    setSelectedLocale(nextLocale);
    setOpen(false);

    resetCart(nextLocale);

    try {
      window.localStorage.setItem(localeCookieKey, nextLocale);
      document.cookie = `${localeCookieKey}=${encodeURIComponent(nextLocale)}; path=/; max-age=31536000; samesite=lax`;
      document.documentElement.lang = getLocaleFileKey(nextLocale);
    } catch {
      // Ignore restricted storage environments.
    }

    startTransition(() => {
      router.replace(buildCurrentUrl(pathname, searchParams));
      router.refresh();
    });
  };

  return (
    <Dropdown
      show={open}
      onToggle={(nextOpen) => setOpen(nextOpen)}
      className={`lang-switcher ${className} ${compactOnMobile ? 'ls-compact-mobile' : ''}`.trim()}
    >
      <Dropdown.Toggle
        variant="transparent"
        id="lang-switcher"
        className="ls-toggle"
        title={ariaLabel}
        aria-label={ariaLabel}
        aria-haspopup="true"
        aria-expanded={open}
        disabled={isPending}
      >
        <span className="ls-flag" aria-hidden="true">
          {activeOption.flag}
        </span>
        <span className="ls-label-text">{activeOption.shortLabel}</span>
      </Dropdown.Toggle>
      <AnimatePresence initial={false}>
        {open ? (
          <Dropdown.Menu
            as={MotionMenu}
            key="lang-menu"
            className="ls-menu rounded-1"
            custom={reduceMotion}
            variants={menuVariants}
            initial="hidden"
            animate="show"
            exit="exit"
            role="menu"
            aria-label={ariaLabel}
          >
            {LOCALE_OPTIONS.map((option) => (
              <Dropdown.Item
                as={motion.button}
                key={option.value}
                type="button"
                className="d-flex align-items-center gap-1 ls-item"
                whileTap={{ scale: reduceMotion ? 1 : 0.96 }}
                aria-current={
                  selectedLocale === option.value ? 'true' : undefined
                }
                onClick={() => handleChange(option.value)}
              >
                <span aria-hidden="true">{option.flag}</span>
                <span>{optionLabels[option.value] || option.value}</span>
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        ) : null}
      </AnimatePresence>
    </Dropdown>
  );
}
